import { useTranslation } from "react-i18next";

interface ScoreGaugeCardProps {
  avgScore: number | null;
  maxScore?: number;
  bestScore: number | null;
  worstScore: number | null;
  trend?: {
    value: number;
    isPositive: boolean;
    label?: string;
  };
}

/**
 * Hero card displaying score with visual gauge and best/worst scores
 */
const ScoreGaugeCard = ({
  avgScore,
  maxScore = 100,
  bestScore,
  worstScore,
  trend,
}: ScoreGaugeCardProps) => {
  const { t } = useTranslation();

  // Calculate percentage for gauge
  const percentage = avgScore != null && maxScore > 0 ? (avgScore / maxScore) * 100 : 0;

  // Determine color based on percentage
  const getGaugeColor = (pct: number): string => {
    if (pct >= 80) return "bg-green-500";
    if (pct >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getTextColor = (pct: number): string => {
    if (pct >= 80) return "text-green-600";
    if (pct >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const gaugeColor = getGaugeColor(percentage);
  const textColor = avgScore != null ? getTextColor(percentage) : "text-gray-400";

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{t("statsNew.module.scoreGlobal")}</h3>
        <div className="p-2 bg-green-100 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </div>
      </div>

      {/* Score value with gauge */}
      <div className="mb-3">
        <div className="flex items-baseline gap-2 mb-2">
          <span className={`text-3xl font-bold ${textColor}`}>
            {avgScore != null ? avgScore.toFixed(1) : "-"}
          </span>
          {maxScore != null && maxScore > 0 && (
            <span className="text-lg text-gray-400">
              / {Number.isInteger(maxScore) ? maxScore : maxScore.toFixed(1)}
            </span>
          )}
        </div>

        {/* Visual gauge */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${gaugeColor} transition-all duration-500 ease-out rounded-full`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>

        {/* Trend */}
        {trend && (
          <div className="flex items-center gap-1.5 mt-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 ${
                trend.isPositive ? "text-green-600" : "text-red-600 rotate-180"
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
            <span
              className={`text-sm font-medium ${
                trend.isPositive ? "text-green-600" : "text-red-600"
              }`}
            >
              {trend.isPositive ? "+" : "-"}
              {trend.value}%
            </span>
            {trend.label && <span className="text-xs text-gray-400">{trend.label}</span>}
          </div>
        )}
      </div>

      {/* Best / Worst scores row */}
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{t("statsNew.module.bestScore")}</span>
          <span className="text-sm font-semibold text-green-600">
            {bestScore != null ? bestScore.toFixed(1) : "-"}
          </span>
        </div>
        <div className="w-px h-4 bg-gray-200" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{t("statsNew.module.avgScore")}</span>
          <span className="text-sm font-semibold text-gray-600">
            {avgScore != null ? avgScore.toFixed(1) : "-"}
          </span>
        </div>
        <div className="w-px h-4 bg-gray-200" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{t("statsNew.module.worstScore")}</span>
          <span className="text-sm font-semibold text-red-600">
            {worstScore != null ? worstScore.toFixed(1) : "-"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScoreGaugeCard;
